<template>
  <div class="card-list-data">
    <div class="row row-cols-1 row-cols-md-2" v-if="!IS_LOADER">
      <div class="col" v-for="(item, index) in branchList" :key="item">
        <div class="iq-widget">
          <input type="radio" :id="item.name + item.id" name="radio" :value="item.id" v-model="branch_id" class="btn-check" @change="onChange" />
          <label :for="item.name + item.id" class="d-block w-100">
            <div class="card iq-branch-box">
              <!-- full-width photo: a salon is picked by how it looks, so the
                   image leads the card instead of sitting in a small avatar -->
              <div class="branch-image">
                <img :src="item.feature_image" alt="feature-image" loading="lazy" />
              </div>
              <div class="card-body">
                <div class="text-center mb-4">
                  <div class="branch-content">
                    <h4 class="mb-1">{{ item.name || '-' }}</h4>
                    <p class="m-0">{{ [item.address?.address_line_1, item.address?.address_line_2, item.address?.city_data?.name].filter(Boolean).join(', ') || '-' }}</p>
                  </div>
                </div>
                <hr />
                <ul class="iq-contact-detail list-inline p-0 m-0">
                  <li class="d-flex align-items-center justify-content-center mb-3">
                    <svg class="text-dark" fill="none" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M11.5317 12.4724C15.5208 16.4604 16.4258 11.8467 18.9656 14.3848C21.4143 16.8328 22.8216 17.3232 19.7192 20.4247C19.3306 20.737 16.8616 24.4943 8.1846 15.8197C-0.493478 7.144 3.26158 4.67244 3.57397 4.28395C6.68387 1.17385 7.16586 2.58938 9.61449 5.03733C12.1544 7.5765 7.54266 8.48441 11.5317 12.4724Z" fill="currentColor" />
                    </svg>
                    <p class="ms-2 mb-0">{{ item.contact_number || '-' }}</p>
                  </li>
                  <li class="d-flex align-items-center justify-content-center">
                    <p class="ms-2 mb-0">
                      {{ [item.address?.state_data?.name, item.address?.postal_code, item.address?.country_data?.name].filter(Boolean).join(', ') || '-' }}
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </label>
        </div>
      </div>
    </div>
    <div v-else class="row row-cols-1 row-cols-md-2">
      <div class="col" v-for="index in 4" :key="index">
        <div class="iq-widget card card-skeleton">
          <div class="skeleton skeleton-image branch-image-skeleton"></div>
          <div class="card-body">
            <div class="text-center mb-4">
              <div>
                <h4 class="skeleton skeleton-title w-50 mb-3 mx-auto"></h4>
                <p class="skeleton skeleton-text w-100 m-0"></p>
              </div>
            </div>
            <hr />
            <ul class="list-inline p-0 m-0">
              <li class="skeleton skeleton-text w-75 m-auto mb-3"></li>
              <li class="skeleton skeleton-text w-100 m-auto"></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="h-100 w-75 d-flex align-items-center justify-content-center" v-if="branchList.length == 0 && !IS_LOADER">{{ $t('quick_booking.msg_no_branches_available') }}</div>
  </div>
  <!-- No footer: picking a branch already advances the wizard (see onChange),
       so a Next button was a second control for something that had just
       happened. It is also the first step, so there is nothing to go back to. -->
</template>
<script setup>
// Library Import
import { ref, onMounted, watch } from 'vue'

// Custom Hooks Import
import { useRequest } from '@/helpers/hooks/useCrudOpration'

// Store Import
import { useQuickBooking } from '../../store/quick-booking'

// URL Constant Import
import { BRANCH_LIST } from '@/vue/constants/quick_booking'

// Props & Emits
const props = defineProps({
  wizardNext: {
    default: '',
    type: [String, Number]
  },
  wizardPrev: {
    default: '',
    type: [String, Number]
  }
})
const emit = defineEmits(['tab-change', 'onReset'])

// List, Store, Update, Get Data Request Hook
const { listingRequest } = useRequest()

// Variables
const branchList = ref([])
const branch_id = ref(null)
const IS_LOADER = ref(true)

// Mounted
onMounted(() => {
  getBranch()
})

// Functions
const getBranch = () => {
  IS_LOADER.value = true
  listingRequest({ url: BRANCH_LIST }).then((res) => {
    IS_LOADER.value = false
    branchList.value = res.data
  })
}

// Store
const store = useQuickBooking()

// On Change Next
const onChange = () => {
  emit('tab-change', props.wizardNext)
}
// Reset Data Function
const resetData = () => {
  branch_id.value = null
}

// Watch
watch(
  () => branch_id.value,
  (value) => {
    store.updateBookingValues({ key: 'branch_id', value: value })
  },
  { deep: true }
)
watch(
  () => store.bookingResponse,
  (value) => {
    resetData()
  },
  { deep: true }
)
</script>
<!-- Scoped styles it will work only on this component-->
<style scoped>
.card-list-data {
  position: relative;
  padding-top: 4px;
  padding-right: 10px;
}

/* equal height cards regardless of address length */
.card-list-data > .row > .col > .iq-widget,
.card-list-data > .row > .col > .iq-widget > label {
  height: 100%;
}

.iq-branch-box {
  height: calc(100% - 1rem);
  cursor: pointer;
  overflow: hidden;
  background-color: #ffffff;
  border: 1.5px solid #edeff2;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(16, 18, 43, 0.04);
  transition: border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  animation: fade-in 0.5s ease-in-out both;
  margin-bottom: 1rem;
}

.iq-branch-box:hover {
  border-color: rgba(var(--bs-primary-rgb), 0.45);
  box-shadow: 0 12px 28px -14px rgba(16, 18, 43, 0.28);
  transform: translateY(-3px);
}

.iq-branch-box::after {
  position: absolute;
  z-index: 2;
  content: '';
  background: var(--bs-primary) url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e") center / 14px 14px no-repeat;
  height: 24px;
  width: 24px;
  border: 2px solid #ffffff;
  top: 12px;
  left: auto;
  right: 12px;
  border-radius: 100%;
  opacity: 0;
  transform: scale(0.7);
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.iq-widget .btn-check:checked + label .iq-branch-box {
  border-color: var(--bs-primary);
  background-color: rgba(var(--bs-primary-rgb), 0.05);
  box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.14);
}

.iq-widget .btn-check:checked + label .iq-branch-box::after {
  opacity: 1;
  transform: scale(1);
}

.iq-branch-box .card-body {
  padding: 22px 20px;
}

.branch-image,
.branch-image-skeleton {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  min-height: 170px;
  background-color: #f3f4f8;
  overflow: hidden;
}

.branch-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.45s cubic-bezier(0.4, 0, 0.2, 1);
}

.iq-branch-box:hover .branch-image img {
  transform: scale(1.04);
}

/* keeps the name readable if a photo runs light at the bottom edge */
.branch-image::after {
  content: '';
  position: absolute;
  inset: auto 0 0 0;
  height: 40%;
  background: linear-gradient(180deg, rgba(16, 18, 43, 0) 0%, rgba(16, 18, 43, 0.14) 100%);
  pointer-events: none;
}

.branch-content h4 {
  font-size: 1.125rem;
  font-weight: 700;
  color: #16182b;
  letter-spacing: -0.01em;
}

.branch-content p {
  font-size: 0.875rem;
  color: #9aa1b1;
}

.iq-branch-box hr {
  margin: 0.875rem 0;
  border: 0;
  border-top: 1px solid #f0f1f6;
}

.iq-contact-detail p {
  font-size: 14px;
  color: #6b7280;
}

.iq-branch-for {
  position: absolute;
  top: 10px;
  left: auto;
  right: 15px;
}
</style>
